import { useMutation, useQuery } from '@apollo/client';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  meWithNotificationQuery,
  setNotificationConfirmedMutation,
} from 'graphql/queries';
import {
  MeWithNotificationQuery,
  SetNotificationConfirmedMutation,
  SetNotificationConfirmedMutationVariables,
} from 'graphql/types';
import colors from 'lib/colors';
import { useState } from 'react';
import { useUID } from 'react-uid';
import { ReactComponent as NotificationIcon } from '../assets/notifications.svg';
import Button from './Button';
import Modal from './Modal';

interface Props {
  userHasConfirmedNotification: boolean;
}

const NotificationCenter = ({ userHasConfirmedNotification }: Props) => {
  const modalHeadingId = useUID();
  const [modalOpened, setModalOpened] = useState(false);
  const { data } = useQuery<MeWithNotificationQuery>(meWithNotificationQuery, {
    notifyOnNetworkStatusChange: true,
    skip: !userHasConfirmedNotification,
  });

  const handleToggleModal = () => setModalOpened((prev) => !prev);

  const [setNotificationConfirmed] = useMutation<
    SetNotificationConfirmedMutation,
    SetNotificationConfirmedMutationVariables
  >(setNotificationConfirmedMutation);

  const notification = data?.me?.UnconfirmedNotifications[0];
  const image = notification?.translation?.imageLink || 'placeholder.png';
  const alt = notification?.type || 'unknown';

  const handleNotificationConfirmed = () => {
    setNotificationConfirmed({
      variables: { input: { notificationId: notification?.id || '' } },
    });
  };
  return (
    <>
      {userHasConfirmedNotification && (
        <NotificationButton onClick={handleToggleModal}>
          <NotificationDot />
          <NotificationIcon
            css={{ color: colors.white, width: 22, height: 22 }}
          />
        </NotificationButton>
      )}

      <NotificationModal
        isOpen={modalOpened}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        onRequestClose={handleToggleModal}
        onAfterClose={handleNotificationConfirmed}
        aria={{
          labelledby: modalHeadingId,
        }}
      >
        <Banner alt={alt} src={image} />
        <Button
          onClick={handleToggleModal}
          labelT={{
            key: 'cardEliminatedModal.buttonText',
            defaultValue: 'Pokračovat',
          }}
          variant="warning"
          css={css`
            margin-top: 3vh;
            padding: 1vh 5vh;
          `}
        />
      </NotificationModal>
    </>
  );
};

export default NotificationCenter;

const NotificationButton = styled('button')({
  position: 'relative',
  border: 'none',
  outline: 'none',
  padding: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: colors.darkBlue,
  height: 45,
  width: 45,
});

const Banner = styled('img')({
  width: '100%',
});

const NotificationModal = styled(Modal)({
  '.Modal': {
    display: 'block',
    textAlign: 'center',
    width: '100%',
    backgroundColor: colors.blueBackground,
    padding: '1vw',
    maxWidth: '30vw',
    '@media screen and (max-width: 1800px)': {
      padding: '2vw',
      maxWidth: '40vw',
    },
    '@media screen and (max-width: 1300px)': {
      padding: '3vw',
      maxWidth: '60vw',
    },
    '@media screen and (max-width: 900px)': {
      maxWidth: '80vw',
    },
  },
});

const NotificationDot = styled('span')({
  position: 'absolute',
  borderRadius: '50%',
  width: 5,
  height: 5,
  backgroundColor: colors.warning,
  top: 13,
  right: 12,
});
